import { useCallback, useContext, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { MBClearButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { LinkedCard } from '@shared/components/blocks/lib/cards';
import { MBInlineCard } from '@shared/components/blocks/lib/cards/MBInlineCard';
import { MBTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1Bold } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';

interface Props {
  card: LinkedCard;
  editable: boolean;
  onRemove(card: LinkedCard): void;
  onChange(card: LinkedCard): void;
  onPressCard?(card: LinkedCard): void;
  onPressBlock?(): void;
}

export function MBCardQuantityRow({
  card,
  editable = false,
  onRemove,
  onChange,
  onPressCard,
  onPressBlock,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const removeCard = useCallback(() => onRemove(card), [onRemove, card]);

  const quantity = card.quantity ?? 0;
  const [value, setValue] = useState(String(quantity));
  const handleChangeQuantity = useCallback(
    (updatedValue: string) => {
      const cleanedValue = updatedValue.replace(/\D/g, '');
      setValue(cleanedValue);

      if (!cleanedValue) {
        return;
      }
      const updatedQuantity = Number(cleanedValue);
      onChange({ ...card, quantity: updatedQuantity });
    },
    [card, onChange],
  );

  useEffect(() => {
    setValue(String(quantity));
  }, [quantity]);

  return (
    <View row paddingLeft={2} paddingRight={3}>
      {editable ? (
        <MBTextInput
          editable={editable}
          value={value}
          size="normal"
          keyboardType="number-pad"
          noPadding
          style={[styles.input, { borderColor: colors.input.borderInactive }]}
          onChangeText={handleChangeQuantity}
        />
      ) : (
        <MBView center style={styles.input}>
          <MBTextBody1Bold color={colors.foregroundActive}>{value}</MBTextBody1Bold>
        </MBView>
      )}
      <MBTouchableOpacity style={styles.cardRow} onPress={onPressBlock}>
        <MBInlineCard card={card} onPress={onPressCard} />
      </MBTouchableOpacity>
      {editable && <MBClearButton onPress={removeCard} />}
    </View>
  );
}

const styles = StyleSheet.create({
  input: {
    height: grid(4.75),
    width: grid(5),
    textAlign: 'center',
    borderRadius: grid(0.5),
  },
  cardRow: {
    alignItems: 'flex-start',
    flex: 1,
    paddingLeft: grid(2),
  },
});
